import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import { Link } from "react-router-dom";
import { useLoading } from "../../../../../shared/context/LoadingContext";
import XLSX from "xlsx-js-style";
import moment from "moment";
import toast from "react-hot-toast";
import "moment/locale/pt-br";
moment.locale("pt-br");

const statusColor = (status) => {
  switch (status) {
    case "enviado":
    case "delivered":
      return "success";
    case "pendente":
      return "warning";
    case "falhou":
    case "failed":
      return "error";
    default:
      return "default";
  }
};

const statusLabel = (status) => {
  switch (status) {
    case "enviado":
    case "delivered":
      return "Enviado";
    case "pendente":
      return "Pendente";
    case "falhou":
    case "failed":
      return "Falhou";
    default:
      return status || "-";
  }
};

function RelatorioSms({
  dataFiltro,
  clinicaId,
  auth,
  acao,
  sendRequest,
  atualizacoes,
}) {
  const { startLoading, stopLoading } = useLoading();
  const [historico, setHistorico] = useState(null);
  const [credito, setCredito] = useState(null);
  const [error, setError] = useState(null);
  const [paginaAtual, setPaginaAtual] = useState(0);
  const [registrosPorPagina, setRegistrosPorPagina] = useState(10);

  useEffect(() => {
    if (acao === "gerar") gerarRelatorio();
    else if (acao === "exportar") exportarRelatorio();
  }, [acao, atualizacoes]);

  const gerarRelatorio = async () => {
    setError(null);
    setHistorico(null);
    setCredito(null);
    startLoading();
    try {
      const headers = { Authorization: "Bearer " + auth.token };

      const [resHistorico, resCredito] = await Promise.all([
        sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/sms/historico/${clinicaId}?dataInicio=${dataFiltro.dataInicial.format("YYYY-MM-DD")}&dataFim=${dataFiltro.dataFinal.format("YYYY-MM-DD")}`,
          "GET",
          null,
          headers,
        ),
        sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/sms/credito/${clinicaId}`,
          "GET",
          null,
          headers,
        ),
      ]);

      setHistorico(resHistorico.logs);
      setCredito(resCredito);
    } catch (err) {
      setError(err.message || "Erro ao carregar relatório de SMS");
    } finally {
      stopLoading();
    }
  };

  const exportarRelatorio = () => {
    if (!historico) {
      toast.error("É necessário gerar o relatório primeiro.");
      return;
    }

    const wb = XLSX.utils.book_new();

    const wsData = [
      ["Relatório de SMS"],
      [
        `Período: ${dataFiltro.dataInicial.format("DD/MM/YYYY")} a ${dataFiltro.dataFinal.format("DD/MM/YYYY")}`,
      ],
      credito
        ? [
            `Saldo atual: ${credito.saldo} ECV | Total gasto: ${credito.totalGasto} ECV`,
          ]
        : [],
      [],
      [
        "Número Destino",
        "Cliente",
        "Status",
        "Tipo",
        "Provider",
        "Tentativas",
        "Enviado em",
        "Confirmado em",
      ],
      ...historico.map((s) => [
        s.numeroDestino || "-",
        s.cliente?.nome || "Desconhecido",
        statusLabel(s.status),
        s.tipoAgendamento || "-",
        s.provider || "-",
        s.tentativas ?? "-",
        s.enviadoEm ? moment(s.enviadoEm).format("DD/MM/YYYY HH:mm") : "-",
        s.confirmadoEm
          ? moment(s.confirmadoEm).format("DD/MM/YYYY HH:mm")
          : "-",
      ]),
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    wb.SheetNames.push("SMS");
    wb.Sheets["SMS"] = ws;

    XLSX.writeFile(wb, `relatorio_sms_${moment().format("YYYY-MM-DD")}.xlsx`);
    toast.success("Arquivo exportado com sucesso!");
  };

  const smsPaginados = historico
    ? historico.slice(
        paginaAtual * registrosPorPagina,
        paginaAtual * registrosPorPagina + registrosPorPagina,
      )
    : [];

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
          Relatório de SMS
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Cartão de crédito */}
        {credito && (
          <Box
            sx={{
              display: "flex",
              gap: 3,
              my: 3,
              flexWrap: "wrap",
            }}
          >
            <Paper
              elevation={2}
              sx={{
                p: 2,
                minWidth: 180,
                borderLeft: "4px solid #4caf50",
                borderRadius: 2,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Saldo disponível
              </Typography>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "#4caf50" }}
              >
                {credito.saldo.toLocaleString("pt-CV")} ECV
              </Typography>
            </Paper>

            <Paper
              elevation={2}
              sx={{
                p: 2,
                minWidth: 180,
                borderLeft: "4px solid #f44336",
                borderRadius: 2,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Total gasto
              </Typography>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "#f44336" }}
              >
                {credito.totalGasto.toLocaleString("pt-CV")} ECV
              </Typography>
            </Paper>

            {historico && (
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  minWidth: 180,
                  borderLeft: "4px solid #1976d2",
                  borderRadius: 2,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  SMS no período
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", color: "#1976d2" }}
                >
                  {historico.length}
                </Typography>
              </Paper>
            )}
          </Box>
        )}

        {/* Tabela */}
        {historico && (
          <>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Número Destino</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Tentativas</TableCell>
                    <TableCell>Enviado em</TableCell>
                    <TableCell>Confirmado em</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {smsPaginados.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ py: 3 }}
                        >
                          Nenhum SMS encontrado no período selecionado.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    smsPaginados.map((s) => (
                      <TableRow key={s._id}>
                        <TableCell>
                          {s.cliente?._id ? (
                            <Link
                              to={`/clientes/${s.cliente._id}`}
                              className="linkNormal"
                            >
                              {s.cliente.nome}
                            </Link>
                          ) : (
                            s.cliente?.nome || "Desconhecido"
                          )}
                        </TableCell>
                        <TableCell>{s.numeroDestino || "-"}</TableCell>
                        <TableCell>
                          <Chip
                            label={statusLabel(s.status)}
                            color={statusColor(s.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{s.tipoAgendamento || "-"}</TableCell>
                        <TableCell>{s.provider || "-"}</TableCell>
                        <TableCell>{s.tentativas ?? "-"}</TableCell>
                        <TableCell>
                          {s.enviadoEm
                            ? moment(s.enviadoEm).format("DD/MM/YYYY HH:mm")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {s.confirmadoEm
                            ? moment(s.confirmadoEm).format("DD/MM/YYYY HH:mm")
                            : "-"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={historico.length}
              page={paginaAtual}
              onPageChange={(e, novaPagina) => setPaginaAtual(novaPagina)}
              rowsPerPage={registrosPorPagina}
              onRowsPerPageChange={(e) => {
                setRegistrosPorPagina(parseInt(e.target.value, 10));
                setPaginaAtual(0);
              }}
              labelRowsPerPage="Registros por página"
            />
          </>
        )}
      </Paper>
    </Box>
  );
}

export default RelatorioSms;
